import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { S } from '../regureStyles';

const SEVERITY_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#16a34a',
};

const RiskSignalTable = ({
  loading,
  filteredSignals,
  selected,
  toggleAll,
  toggleSelect,
  fmtDate,
  handleAcknowledge,
  actionLoading,
  handleResolve,
  setDismissTarget,
  SIGNAL_LABELS,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const severityLabel = sev => {
    switch (sev) {
      case 'critical': return t('screens.riskSignals.sevCritical');
      case 'high': return t('screens.riskSignals.sevHigh');
      case 'medium': return t('screens.riskSignals.sevMedium');
      case 'low': return t('screens.riskSignals.sevLow');
      default: return sev;
    }
  };
  const statusLabel = st => {
    switch (st) {
      case 'active': return t('screens.riskSignals.statusActive');
      case 'acknowledged': return t('screens.riskSignals.statusAcknowledged');
      case 'dismissed': return t('screens.riskSignals.statusDismissed');
      case 'resolved': return t('screens.riskSignals.statusResolved');
      default: return st;
    }
  };

  const headers = [
    t('screens.riskSignals.colSeverity'),
    t('screens.riskSignals.colType'),
    t('screens.riskSignals.colClaimNumber'),
    t('screens.riskSignals.colClaimant'),
    t('screens.riskSignals.colDescription'),
    t('screens.riskSignals.colCreated'),
    t('screens.riskSignals.colStatus'),
    t('screens.riskSignals.colActions'),
  ];

  return (
    <div style={{ flex: 1, padding: '20px 32px 0', overflowX: 'auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filteredSignals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <svg
            width='40'
            height='40'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#cbd5e1'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{ marginBottom: 12 }}
          >
            <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
            <path d='M9 12l2 2 4-4' />
          </svg>
          <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{t('screens.riskSignals.emptyTitle')}</div>
          <div style={{ fontSize: 13, color: S.textMuted, marginTop: 4 }}>{t('screens.riskSignals.emptyDesc')}</div>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 160 }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: `2px solid ${S.borderLight}` }}>
              <th style={{ padding: '10px 8px', textAlign: 'center' }}>
                <input type='checkbox' checked={selected.size === filteredSignals.length && filteredSignals.length > 0} onChange={toggleAll} />
              </th>
              {headers.map(h => (
                <th
                  key={h}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: 11,
                    fontWeight: 700,
                    color: S.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSignals.map((sig, i) => {
              const color = SEVERITY_COLORS[sig.severity] || '#64748b';
              const claimNum = sig.claim?.claimNumber || '—';
              const claimant = sig.claim?.claimant?.name || '—';
              return (
                <tr
                  key={sig._id || `synthetic-${i}`}
                  style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
                >
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    <input type='checkbox' checked={selected.has(sig._id)} onChange={() => toggleSelect(sig._id)} onClick={e => e.stopPropagation()} />
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color, textTransform: 'capitalize' }}>{severityLabel(sig.severity)}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', fontSize: 12, fontWeight: 600, color: S.textPrimary }}>{SIGNAL_LABELS[sig.type] || sig.type}</td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => history.push(`/claims/${claimNum}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        color: S.burgundy,
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      {claimNum}
                    </button>
                  </td>
                  <td style={{ padding: '12px', fontSize: 13, color: S.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {claimant}
                  </td>
                  <td style={{ padding: '12px', fontSize: 12, color: S.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sig.description}
                  </td>
                  <td style={{ padding: '12px', fontSize: 12, color: S.textMuted, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtDate(sig.createdAt)}</td>
                  <td style={{ padding: '12px' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        background: sig.status === 'active' ? '#fee2e2' : sig.status === 'acknowledged' ? '#dcfce7' : sig.status === 'dismissed' ? '#f1f5f9' : '#dbeafe',
                        color: sig.status === 'active' ? '#991b1b' : sig.status === 'acknowledged' ? '#15803d' : sig.status === 'dismissed' ? '#64748b' : '#1d4ed8',
                      }}
                    >
                      {statusLabel(sig.status)}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {(sig.status === 'active' || sig.status === 'acknowledged') && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handleAcknowledge(sig._id)}
                          disabled={actionLoading === sig._id}
                          style={{
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            background: 'rgba(128,0,32,0.06)',
                            border: `1px solid rgba(128,0,32,0.2)`,
                            borderRadius: 6,
                            color: S.burgundy,
                            cursor: 'pointer',
                            opacity: actionLoading === sig._id ? 0.5 : 1,
                          }}
                        >
                          {t('screens.riskSignals.actionAck')}
                        </button>
                        <button
                          onClick={() => handleResolve(sig._id)}
                          disabled={actionLoading === sig._id}
                          style={{
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            background: 'rgba(16,185,129,0.06)',
                            border: `1px solid rgba(16,185,129,0.2)`,
                            borderRadius: 6,
                            color: '#10b981',
                            cursor: 'pointer',
                            opacity: actionLoading === sig._id ? 0.5 : 1,
                          }}
                        >
                          {t('screens.riskSignals.actionResolve')}
                        </button>
                        <button
                          onClick={() => setDismissTarget(sig)}
                          style={{
                            padding: '4px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            background: S.bgCard,
                            border: `1px solid ${S.borderLight}`,
                            borderRadius: 6,
                            color: S.textSecondary,
                            cursor: 'pointer',
                          }}
                        >
                          {t('screens.riskSignals.actionDismiss')}
                        </button>
                      </div>
                    )}
                    {sig.status === 'dismissed' && sig.dismissReason && (
                      <div
                        title={sig.dismissReason}
                        style={{ fontSize: 11, color: S.textMuted, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {sig.dismissReason}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RiskSignalTable;
